import React from 'react';
import {
  Box,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { DevicePartition, DeviceStorage } from '../types';
import { formatBytes, useI18n } from '../i18n';

interface StorageLayoutProps {
  storage?: DeviceStorage;
}

interface MapSegment {
  key: string;
  label: string;
  address: number;
  size: number;
  partition?: DevicePartition;
  gap?: 'bootloader' | 'unused';
}

function formatHex(n: number): string {
  return `0x${n.toString(16).toUpperCase()}`;
}

function partitionMapEnd(partitions: DevicePartition[], flashSize?: number): number {
  const fromTable = partitions.reduce((end, p) => Math.max(end, (p.address || 0) + (p.size || 0)), 0);
  return Math.max(fromTable, flashSize || 0);
}

function partitionColor(segment: MapSegment, theme: Theme): string {
  if (segment.gap === 'unused') {
    return theme.palette.action.disabledBackground;
  }
  if (segment.gap === 'bootloader') {
    return theme.palette.grey[500];
  }
  const p = segment.partition;
  if (!p) {
    return theme.palette.grey[400];
  }
  if (p.running) {
    return theme.palette.primary.main;
  }
  if (p.type === 'app') {
    return theme.palette.primary.light;
  }
  if (p.subtype === 'spiffs') {
    return theme.palette.success.main;
  }
  if (p.subtype === 'ota') {
    return theme.palette.warning.main;
  }
  if (p.subtype === 'coredump') {
    return theme.palette.secondary.main;
  }
  if (p.subtype === 'nvs') {
    return theme.palette.info.light;
  }
  return theme.palette.grey[400];
}

function buildSegments(partitions: DevicePartition[], mapSize: number): MapSegment[] {
  const sorted = [...partitions].sort((a, b) => (a.address || 0) - (b.address || 0));
  const out: MapSegment[] = [];
  let cursor = 0;
  sorted.forEach((p, index) => {
    const address = p.address || 0;
    const size = p.size || 0;
    if (address > cursor) {
      out.push({
        key: `gap-${cursor}`,
        label: cursor === 0 ? 'bootloader' : 'unused',
        address: cursor,
        size: address - cursor,
        gap: cursor === 0 ? 'bootloader' : 'unused',
      });
    }
    out.push({
      key: `${p.label || 'part'}-${index}`,
      label: p.label || p.subtype || 'part',
      address,
      size,
      partition: p,
    });
    cursor = address + size;
  });
  if (mapSize > cursor) {
    out.push({
      key: `gap-${cursor}`,
      label: 'unused',
      address: cursor,
      size: mapSize - cursor,
      gap: 'unused',
    });
  }
  return out.filter((s) => s.size > 0);
}

const StorageLayout: React.FC<StorageLayoutProps> = ({ storage }) => {
  const { t } = useI18n();
  const theme = useTheme<Theme>();
  const partitions = storage?.partitions || [];
  const hasLayout = partitions.length > 0 || Boolean(storage?.flashChipSize || storage?.flashSize);

  if (!storage || !hasLayout) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('detail.storageEmpty')}
      </Typography>
    );
  }

  const mapSize = partitionMapEnd(partitions, storage.flashSize);
  const chipSize = storage.flashChipSize || mapSize;
  const unusedChip = Math.max(0, chipSize - mapSize);
  const segments = buildSegments(partitions, mapSize);
  const spiffsTotal = storage.spiffsTotal || 0;
  const spiffsUsed = storage.spiffsUsed || 0;
  const spiffsPct = spiffsTotal > 0 ? Math.min(100, (spiffsUsed / spiffsTotal) * 100) : 0;
  const runningPart = partitions.find((p) => p.running) || partitions.find((p) => p.label === storage.runningPartition);
  const sketchPct = runningPart && storage.sketchSize
    ? Math.min(100, (storage.sketchSize / runningPart.size) * 100)
    : 0;

  const segmentCaption = (segment: MapSegment) => {
    const name = segment.gap === 'bootloader'
      ? t('detail.bootloader')
      : segment.gap === 'unused'
        ? t('detail.flashUnused')
        : segment.label;
    return `${name} · ${formatHex(segment.address)} · ${formatBytes(segment.size)}`;
  };

  return (
    <Box>
      <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
        {chipSize > 0 && (
          <Chip size="small" label={`${t('detail.flashChip')}: ${formatBytes(chipSize)}`} />
        )}
        {mapSize > 0 && (
          <Chip size="small" variant="outlined" label={`${t('detail.flashMap')}: ${formatBytes(mapSize)}`} />
        )}
        {unusedChip > 0 && chipSize !== mapSize && (
          <Chip
            size="small"
            variant="outlined"
            label={`${t('detail.flashUnused')}: ${formatBytes(unusedChip)}`}
          />
        )}
        {storage.runningPartition && (
          <Chip
            size="small"
            color="primary"
            label={`${t('detail.runningSlot')}: ${storage.runningPartition}`}
          />
        )}
      </Box>
      {unusedChip > 0 && chipSize !== mapSize && (
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          {t('detail.mapVsChip', { map: formatBytes(mapSize), chip: formatBytes(chipSize) })}
        </Typography>
      )}

      {spiffsTotal > 0 && (
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2">{t('detail.spiffs')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('detail.spiffsUsage', { used: formatBytes(spiffsUsed), total: formatBytes(spiffsTotal) })}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={spiffsPct} sx={{ height: 8, borderRadius: 1 }} />
        </Box>
      )}

      {runningPart && storage.sketchSize ? (
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2">{t('detail.sketch')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('detail.spiffsUsage', {
                used: formatBytes(storage.sketchSize),
                total: formatBytes(runningPart.size),
              })}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={sketchPct}
            color="primary"
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Box>
      ) : null}

      {segments.length > 0 && (
        <Box
          display="flex"
          height={28}
          borderRadius={1}
          overflow="hidden"
          mb={2}
          sx={{ border: 1, borderColor: 'divider' }}
        >
          {segments.map((segment) => (
            <Tooltip key={segment.key} title={segmentCaption(segment)} arrow>
              <Box
                sx={{
                  flexGrow: segment.size,
                  flexBasis: 0,
                  minWidth: 6,
                  bgcolor: partitionColor(segment, theme),
                  borderRight: 1,
                  borderColor: 'background.paper',
                  cursor: 'default',
                }}
              />
            </Tooltip>
          ))}
        </Box>
      )}

      {partitions.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('detail.partition')}</TableCell>
              <TableCell>{t('detail.kind')}</TableCell>
              <TableCell>{t('detail.offset')}</TableCell>
              <TableCell align="right">{t('detail.size')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {segments.filter((s) => s.partition).map((segment) => {
              const p = segment.partition!;
              return (
                <TableRow key={segment.key} selected={Boolean(p.running)}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {p.label || t('common.na')}
                      {p.running && (
                        <Chip size="small" color="primary" label={t('detail.runningSlot')} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {[p.type, p.subtype].filter(Boolean).join(' / ') || t('common.na')}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{formatHex(p.address)}</TableCell>
                  <TableCell align="right">
                    {formatBytes(p.size)}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {p.size.toLocaleString()} B
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </Box>
      )}
    </Box>
  );
};

export default StorageLayout;
